import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, LoadingOverlay, Paper, Title } from "@mantine/core";
import { Client, ClientBillingInput } from "src/graphql/generated/graphql";
import { handleError } from "src/utils/error";

import { useUpdateClientBilling } from "../../../../hooks/clients";
import {
    ClientBillingForm,
    ClientBillingFormProvider,
    parseClientBillingInput,
    useClientBillingForm,
} from "../../../ClientBillingForm";

export interface ClientBillingProps {
    client: Client;
    isLoading?: boolean;
    onClientUpdate?();
}

export const ClientBilling = ({ client, isLoading, onClientUpdate }: ClientBillingProps) => {
    const { t } = useTranslation(["common"]);

    const billingForm = useClientBillingForm({});

    useEffect(() => {
        if (client) {
            const values = parseClientBillingInput(client?.billing);
            billingForm.setValues(values);
            billingForm.resetDirty(values);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(client?.billing)]);

    const { updateClientBilling, isUpdatingClientBilling } = useUpdateClientBilling(client?.id);

    const handleSubmit = useCallback(
        async (billing: ClientBillingInput) => {
            try {
                await updateClientBilling(billing);
                onClientUpdate?.();
            } catch (err) {
                handleError(err, {
                    message: { key: "client_billing_saving_failed", namespace: "common" },
                    context: { clientId: client?.id },
                });
            }
        },
        [client?.id, onClientUpdate, updateClientBilling]
    );

    return (
        <ClientBillingFormProvider form={billingForm}>
            <Paper component="form" pos="relative" p={36} radius={12} onSubmit={billingForm.onSubmit(handleSubmit)}>
                <LoadingOverlay visible={isLoading || isUpdatingClientBilling} />
                <Title mb={28} order={3} size={16} weight={600}>
                    {t("billing_details")}
                </Title>
                <ClientBillingForm />
                <Group mt="xl" position="right">
                    <Button
                        type="submit"
                        size="lg"
                        radius="md"
                        color="primary.main"
                        variant="filled"
                        style={{
                            fontSize: 14,
                        }}
                    >
                        {t("client_billing_save")}
                    </Button>
                </Group>
            </Paper>
        </ClientBillingFormProvider>
    );
};
